import { CircleAlert, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { OrganizationSupportChatDialog } from "@/features/chat/organization-support-chat-dialog";
import { useTRPC } from "@/trpc";
import { useRevealProvider } from "./use-reveal-provider";
import {
  parseRevealProviderError,
  REVEAL_PROVIDER_FAILURE_MESSAGES,
  RevealProviderFailure,
} from "./reveal-provider-error";

const UPGRADE_MESSAGE =
  "Hi Saile, our organization has reached its monthly provider reveal limit. We'd like to discuss upgrading our account.";

type RevealProviderDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerUserId: string;
  organizationBusinessUnitId: string;
  initialMonthlyRevealLimit?: number | null;
  onRevealed?: () => void;
};

export function RevealProviderDialog({
  open,
  onOpenChange,
  providerUserId,
  organizationBusinessUnitId,
  initialMonthlyRevealLimit = null,
  onRevealed,
}: RevealProviderDialogProps) {
  const [limitReached, setLimitReached] = useState(false);
  const [monthlyRevealLimit, setMonthlyRevealLimit] = useState<number | null>(
    null,
  );
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [supportChatOpen, setSupportChatOpen] = useState(false);
  const [supportThreadId, setSupportThreadId] = useState<string | null>(null);
  const trpc = useTRPC();

  useEffect(() => {
    if (!open) {
      return;
    }
    setMonthlyRevealLimit(initialMonthlyRevealLimit);
    setLimitReached(initialMonthlyRevealLimit !== null);
    setFailureMessage(null);
  }, [initialMonthlyRevealLimit, open]);

  const supportThreadMutation = useMutation(
    trpc.chat.orgGetOrCreateSupportThread.mutationOptions({
      onSuccess: ({ id }) => {
        setLimitReached(false);
        setSupportThreadId(id);
        onOpenChange(false);
        setSupportChatOpen(true);
      },
      onError: () => {
        toast.error("Unable to open Saile chat. Please try again.");
      },
    }),
  );
  const revealMutation = useRevealProvider({
    onSuccess: () => {
      onOpenChange(false);
      onRevealed?.();
    },
    onError: (error) => {
      const { failure, monthlyLimit } = parseRevealProviderError(error);
      if (failure === RevealProviderFailure.LimitReached) {
        setMonthlyRevealLimit(monthlyLimit);
        setLimitReached(true);
        return;
      }
      if (failure === RevealProviderFailure.ProviderNotInBusinessUnit) {
        toast.error(REVEAL_PROVIDER_FAILURE_MESSAGES[failure]);
        onOpenChange(false);
        return;
      }
      setFailureMessage(REVEAL_PROVIDER_FAILURE_MESSAGES[failure]);
    },
  });

  return (
    <>
      <AlertDialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setLimitReached(false);
            setMonthlyRevealLimit(null);
            setFailureMessage(null);
          }
          onOpenChange(nextOpen);
        }}
      >
        <AlertDialogContent className="!w-[calc(100vw-2rem)] !max-w-lg !max-h-[calc(100dvh-2rem)] gap-5 overflow-y-auto rounded-2xl border-slate-200 bg-white p-5 text-slate-900 sm:rounded-2xl sm:p-6">
          <AlertDialogHeader className="min-w-0 space-y-3 text-left">
            <AlertDialogTitle className="text-xl leading-7 [overflow-wrap:anywhere]">
              {limitReached
                ? "Monthly Reveal Limit Reached"
                : "Reveal Provider Details?"}
            </AlertDialogTitle>
            {limitReached ? (
              <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left">
                <CircleAlert
                  aria-hidden="true"
                  className="mt-0.5 h-5 w-5 shrink-0 text-amber-700"
                />
                <div className="min-w-0 space-y-2">
                  <p className="text-sm font-medium leading-6 text-amber-950">
                    Your organization has reached its monthly reveal limit.
                  </p>
                  <AlertDialogDescription className="text-sm leading-6 text-amber-900">
                    {monthlyRevealLimit !== null
                      ? `Your plan includes ${monthlyRevealLimit} provider ${
                          monthlyRevealLimit === 1 ? "reveal" : "reveals"
                        } per month. `
                      : ""}
                    Contact Saile to discuss upgrading your account and continue
                    revealing providers.
                  </AlertDialogDescription>
                </div>
              </div>
            ) : (
              <>
                <AlertDialogDescription className="text-sm leading-6 text-slate-600">
                  This will permanently reveal this provider&apos;s identity to
                  your organization. This action cannot be undone — once
                  revealed, the provider&apos;s name and profile details will
                  always be visible to your team.
                </AlertDialogDescription>
                {failureMessage && (
                  <p
                    role="alert"
                    className="text-sm font-medium leading-6 text-destructive"
                  >
                    {failureMessage}
                  </p>
                )}
              </>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 border-t border-slate-200 pt-4 sm:flex-wrap sm:space-x-0">
            {limitReached ? (
              <>
                <AlertDialogCancel className="mt-0 w-full sm:w-auto">
                  Close
                </AlertDialogCancel>
                <Button
                  className="w-full gap-2 sm:w-auto"
                  disabled={supportThreadMutation.isPending}
                  onClick={() =>
                    supportThreadMutation.mutate({
                      organizationBusinessUnitId,
                    })
                  }
                >
                  {supportThreadMutation.isPending && (
                    <Loader2
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0 animate-spin"
                    />
                  )}
                  Contact Saile
                </Button>
              </>
            ) : (
              <>
                <AlertDialogCancel
                  className="mt-0 w-full sm:w-auto"
                  disabled={revealMutation.isPending}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  className="w-full gap-2 sm:w-auto"
                  disabled={revealMutation.isPending}
                  onClick={(event) => {
                    event.preventDefault();
                    setFailureMessage(null);
                    revealMutation.mutate({
                      organizationBusinessUnitId,
                      providerUserId,
                    });
                  }}
                >
                  {revealMutation.isPending && (
                    <Loader2
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0 animate-spin"
                    />
                  )}
                  Reveal Provider Details
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <OrganizationSupportChatDialog
        open={supportChatOpen}
        onOpenChange={setSupportChatOpen}
        threadId={supportThreadId}
        initialMessage={UPGRADE_MESSAGE}
      />
    </>
  );
}
